import { useMainStore } from '../stores/mainStore.js';
import tinycolor from 'tinycolor2';
import { getAccessibleColor } from '../helpers/colors.js';

/**
 * Builds the CSS custom properties that theme buttons, search pills and dropdowns from an org's
 * configurable primary color. Because that color is arbitrary, the raw value cannot be trusted to
 * meet WCAG contrast on its own: focus indicators and pill/dropdown text are derived through
 * `getAccessibleColor` against the surfaces they actually render on, and alpha-blended "active"
 * fills are flattened to opaque colors first so the contrast math has a real rendered color.
 *
 * @returns {{themedButtons: Object<string, string>}} An object whose `themedButtons` maps CSS
 *   variable names (e.g. `--secondary-button-focus-outline`, `--pill-dropdown-text-contrast`) to
 *   their computed values, ready to bind as an inline style.
 */
export const useThemedButtons = () => {
  const mainStore = useMainStore();
  const primaryColor = tinycolor(mainStore.globals?.info?.primary_color?.hex ?? '#000000').toHslString();
  const secondaryActiveBackground = tinycolor(primaryColor).setAlpha(0.3);
  const secondaryActiveBackgroundHsla = secondaryActiveBackground.toHslString();
  const primaryTextContrast = tinycolor
    .mostReadable(primaryColor, ['#fff', secondaryActiveBackground, '#000'])
    .toHexString();
  // Org-configurable primary color can be any hue/lightness, so focus indicators need a
  // contrast-adjusted variant to reliably meet WCAG 1.4.11 (3:1 non-text contrast). The rings are
  // not all drawn on white: `outline-offset: 1px` puts the pill ring on the page or card surface
  // (#f7f7f7 on athletics/event cards, #fffaf4 on document cards), while the focused dropdown
  // option draws its ring inside itself, over a 10% primary tint. Solving for the darkest of those
  // surfaces satisfies the lighter ones too, since the adjusted color is darker than all of them.
  // SO-3775.
  const focusSurfaces = [
    '#ffffff',
    '#f7f7f7',
    '#fffaf4',
    tinycolor.mix(tinycolor('#ffffff'), tinycolor(primaryColor), 10).toHexString(),
  ];
  const darkestFocusSurface = focusSurfaces.reduce((a, b) =>
    tinycolor(a).getLuminance() <= tinycolor(b).getLuminance() ? a : b,
  );
  const focusIndicatorColor = getAccessibleColor(primaryColor, darkestFocusSurface, 3);
  // Same rationale for text: raw primaryColor used as text color has no contrast floor against
  // the white/near-white surfaces it renders on, so it can fail WCAG 1.4.3 (4.5:1) for an org
  // with a light primary color. Flatten the alpha-blended "active" pill background to an opaque
  // color first, since contrast math needs an actual rendered color, not a translucent one.
  const secondaryActiveBackgroundFlattened = tinycolor
    .mix(tinycolor('#ffffff'), tinycolor(primaryColor), 30)
    .toHexString();
  const pillDropdownTextColor = getAccessibleColor(primaryColor, secondaryActiveBackgroundFlattened, 4.5);

  const themedButtons = {
    '--primary-button-background': primaryColor,
    '--primary-button-text-contrast': primaryTextContrast,
    '--primary-button-border': 'none',
    '--primary-button-border-radius': '9999px',
    '--primary-button-hover': `${tinycolor(primaryColor).lighten(7)}`,
    '--primary-button-focus-outline': '0.75px solid #000FFF',
    '--secondary-button-text': primaryColor,
    '--secondary-button-text-contrast': tinycolor
      .mostReadable(secondaryActiveBackgroundHsla, ['#000', primaryColor, '#fff'])
      .toHexString(),
    '--secondary-button-background': '#FFFFFF',
    '--secondary-button-border': `0.75px solid ${primaryColor}`,
    '--secondary-button-border-radius': '9999px',
    '--secondary-button-hover': tinycolor(primaryColor).setAlpha(0.2),
    '--secondary-button-hover-border': `0.75px solid ${tinycolor(primaryColor).setAlpha(0.5)}`,
    '--secondary-button-focus-outline': `0.75px solid ${focusIndicatorColor}`,
    '--secondary-button-active': secondaryActiveBackgroundHsla,

    '--tertiary-button-background': tinycolor(primaryColor).setAlpha(0.1),
    '--tertiary-button-text-contrast': getAccessibleColor(primaryColor, '#ffffff', 4.5),
    '--tertiary-button-border': `0.75px solid ${tinycolor(primaryColor).setAlpha(0.3)}`,
    '--tertiary-button-border-radius': '9999px',
    '--tertiary-button-hover': tinycolor(primaryColor).setAlpha(0.3),
    '--tertiary-button-hover-border': `0.75px solid ${tinycolor(primaryColor).setAlpha(0.5)}`,
    '--tertiary-button-focus-outline': `0.75px solid ${focusIndicatorColor}`,
    '--tertiary-button-active': tinycolor(primaryColor).setAlpha(0.5),
    '--tertiary-button-active-border': `0.75px solid ${primaryColor}`,

    '--disabled-button-background': '#E0E0E0',
    '--disabled-button-text-contrast': '#616161',
    '--disabled-button-tertiary-text-contrast': '#949494',

    '--focus-indicator-color': focusIndicatorColor,
    '--pill-dropdown-text-contrast': pillDropdownTextColor,
  };
  return {
    themedButtons,
  };
};
